import { Service, type SWRConfiguration, useGet } from "@api/common";
import { useGymOptoutRate } from "./gym";
import type {
  CustomerEventMetric,
  CustomerMetric,
  CountMetric,
  FlowStateMetric,
  FormSubmissionMetric,
  FunnelSnapshot,
  LandingPageFunnel,
  GroupedCountMetric,
  GroupedFlowStateMetric,
  GroupedMessageMetric,
  ListParams,
  MembersSnapshot,
  MessageMetric,
  MetricBody,
} from "./types";

export type { CustomerMetric, MessageMetric, MetricBody };

export class MetricService extends Service<MessageMetric> {
  constructor() {
    super("/metrics-v2");
  }
}

export const metricService = new MetricService();

export function useConversionsMetrics(params?: ListParams<MetricBody>, config?: SWRConfiguration) {
  return useGet<any[]>(metricService, "/metrics-v2/conversions", params, config);
}

export function useFormSubmissionsMetrics(
  params?: ListParams<MetricBody>,
  config?: SWRConfiguration,
) {
  return useGet<FormSubmissionMetric[]>(metricService, "/metrics/form-submissions", params, config);
}

export function useFormSubmissionsMetricsV2(
  params?: ListParams<MetricBody>,
  config?: SWRConfiguration,
) {
  return useGet<CountMetric[]>(metricService, "/metrics-v2/form-submissions", params, config);
}

export function useGroupedFormSubmissionsMetrics(
  params?: ListParams<MetricBody> & { group?: string },
  config?: SWRConfiguration,
) {
  return useGet<GroupedCountMetric[]>(
    metricService,
    "/metrics-v2/form-submissions",
    params,
    config,
  );
}

export function useFormViewsMetrics(params?: ListParams<MetricBody>, config?: SWRConfiguration) {
  return useGet<CountMetric[]>(metricService, "/metrics-v2/form-views", params, config);
}

export function useLandingPageViewsMetrics(
  params?: ListParams<MetricBody> & { landingPage?: string },
  config?: SWRConfiguration,
) {
  return useGet<CountMetric[]>(metricService, "/metrics-v2/landing-page-views", params, config);
}

export function useLandingPageClicksMetrics(
  params?: ListParams<MetricBody> & { landingPage?: string; cta?: string },
  config?: SWRConfiguration,
) {
  return useGet<CountMetric[]>(metricService, "/metrics-v2/landing-page-clicks", params, config);
}

export function useLandingPageFunnel(
  params: { gym?: string; landingPage?: string; startDate?: string; endDate?: string },
  config?: SWRConfiguration,
) {
  return useGet<LandingPageFunnel>(
    metricService,
    params.gym && params.landingPage && params.startDate && params.endDate
      ? "/metrics-v2/landing-page-funnel"
      : null,
    params,
    config,
  );
}

export function useMessagesMetrics(params?: ListParams<MetricBody>, config?: SWRConfiguration) {
  return useGet<MessageMetric[]>(metricService, "/metrics/messages", params, config);
}

export function useContactsMetrics(params?: ListParams<MetricBody>, config?: SWRConfiguration) {
  return useGet<CustomerMetric[]>(metricService, "/metrics/customers", params, config);
}

export function useFlowStatesMetrics(params?: ListParams<MetricBody>, config?: SWRConfiguration) {
  return useGet<FlowStateMetric[]>(metricService, "/metrics-v2/flow-states", params, config);
}

export function useNodeFlowStatesMetrics(
  params?: ListParams<MetricBody> & { group?: string },
  config?: SWRConfiguration,
) {
  return useGet<GroupedFlowStateMetric[]>(metricService, "/metrics-v2/flow-states", params, config);
}

export function useNodeMessagesMetrics(
  params?: ListParams<MetricBody> & { group?: string; flowNode?: string },
  config?: SWRConfiguration,
) {
  return useGet<GroupedMessageMetric[]>(metricService, "/metrics-v2/messages", params, config);
}

/**
 * General-purpose messages metrics query. Extra params (protocol, direction,
 * etc.) are passed through to the API as Mongo filters, and `group` returns a
 * per-group breakdown (e.g. `group=user` or `group=protocol`) in each bucket.
 */
export function useMessagesMetricsV2(
  params?: ListParams<MetricBody> & {
    group?: string;
    protocol?: string;
    direction?: string;
  },
  config?: SWRConfiguration,
) {
  return useGet<GroupedMessageMetric[]>(metricService, "/metrics-v2/messages", params, config);
}

/**
 * Customer event (booking/attendance) time-series. Extra params (`event`,
 * `type`, etc.) pass through as Mongo filters; `group` returns per-group
 * breakdowns in each bucket.
 */
export function useCustomerEventsMetrics(
  params?: ListParams<MetricBody> & { group?: string; event?: string },
  config?: SWRConfiguration,
) {
  return useGet<CustomerEventMetric[]>(
    metricService,
    "/metrics-v2/customer-events",
    params,
    config,
  );
}

/**
 * Membership snapshot for a gym. The `total`/`active`/`statuses`/`memberships`
 * figures are point-in-time; passing a date range adds range-scoped churn
 * (`started`/`cancelled`). Both dates must be supplied together.
 */
export function useMembersSnapshot(
  params: { gym?: string; startDate?: string; endDate?: string },
  config?: SWRConfiguration,
) {
  return useGet<MembersSnapshot>(
    metricService,
    params.gym ? "/metrics-v2/members" : null,
    params,
    config,
  );
}

/** Lead-cohort sales funnel for leads created within the date range. */
export function useFunnelMetrics(
  params: { gym?: string; startDate: string; endDate: string },
  config?: SWRConfiguration,
) {
  return useGet<FunnelSnapshot>(
    metricService,
    params.gym ? "/metrics-v2/funnel" : null,
    params,
    config,
  );
}

export function useDashboardOptOut(gym: string | undefined) {
  const { data } = useGymOptoutRate(gym);
  return {
    past: {
      rate: 0,
      count: 0,
    },
    current: {
      rate: data?.rate ?? 0,
      count: 0,
    },
  };
}
